// `/admin/withdrawals` (croqui `screens/admin/saques.tsx`). Foco declarado
// (`spec/17` §8.2): **a fila `awaiting`**, a tela abre nela, ordenada do mais antigo
// para o mais novo pela própria API (`created_at ASC` em `status=awaiting`).
//
// Esta é a rota-PAI: ela desenha a tela e, por cima, o `RouteDrawer` com o `<Outlet/>` da
// rota `$id` (`spec/24` §1 decisão 5). Deep link em `.../wd_…` monta a fila embaixo e o
// drawer aberto; fechar volta para cá preservando o `search`.
//
// O `403 mfa_required` do `POST /admin/v1/withdrawals/{id}/approve` não é tratado aqui:
// `api/client.ts` abre o `StepUpDialog` e reexecuta a chamada (`spec/17` §4.3).

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { errorMessageOf } from '../api/errors.ts';
import {
  type AdminWithdrawal,
  approveWithdrawal,
  payoutsPausedQueryOptions,
} from '../api/queries/admin-withdrawals.ts';
import { ErrorState } from '../components/app/error-state.tsx';
import { Mono } from '../components/kit/mono.tsx';
import { Notice } from '../components/kit/notice.tsx';
import { PageHeader } from '../components/kit/page-header.tsx';
import { Panel } from '../components/kit/panel.tsx';
import { RouteDrawer } from '../components/kit/route-drawer.tsx';
import { adminWithdrawalsWithTotalsQueryOptions } from '../features/admin-withdrawals/queries.ts';
import { TAB_IDS, type TabId } from '../features/admin-withdrawals/rules.ts';
import {
  AdminWithdrawalsQueue,
  PayoutsPausedBanner,
  QUEUE_TAB,
} from '../features/admin-withdrawals/withdrawals-queue.tsx';

/**
 * A aba é o `status` da URL: os links que já existiam (`?status=approved`) continuam
 * valendo, e `all` é um valor do search, não a ausência dele, com `status` opcional,
 * "Todos" não teria como ser representado.
 */
const searchSchema = z.object({
  status: z.enum(TAB_IDS).default(QUEUE_TAB).catch(QUEUE_TAB),
  seller_id: z.string().optional(),
});

function AdminWithdrawals() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { status: tab, seller_id: sellerId } = Route.useSearch();

  const filters = {
    ...(tab === 'all' ? {} : { status: tab }),
    ...(sellerId === undefined ? {} : { seller_id: sellerId }),
  };
  const list = useInfiniteQuery(adminWithdrawalsWithTotalsQueryOptions(filters));
  const paused = useQuery(payoutsPausedQueryOptions());

  const approve = useMutation({
    mutationFn: (withdrawal: AdminWithdrawal) => approveWithdrawal(withdrawal.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
    },
  });

  const rows = list.data?.pages.flatMap((page) => page.data) ?? [];
  // `totals` vem na MESMA transação da página (API-27): a última é a mais fresca.
  const totals = list.data?.pages.at(-1)?.totals;

  // A rota `$id` é filha desta: se ela casa, o detalhe está aberto por cima da fila.
  const detail = useMatch({ from: '/admin/withdrawals/$id', shouldThrow: false });

  const go = (next: { status?: TabId; seller_id?: string }): void => {
    void navigate({
      to: '/admin/withdrawals',
      search: {
        status: next.status ?? tab,
        ...(next.seller_id === undefined ? {} : { seller_id: next.seller_id }),
      },
    });
  };

  return (
    <>
      <PageHeader
        title="Saques"
        sub="Fila de aprovação e histórico de todos os sellers"
        path="/admin/v1/withdrawals"
        size="lg"
      />

      {/* Kill switch ligado: o banner é o primeiro que o operador lê. */}
      {paused.data?.paused === true && (
        <PayoutsPausedBanner
          reason={paused.data.reason}
          onOpenSettings={() => {
            void navigate({ to: '/admin/settings' });
          }}
        />
      )}
      {/* Falhar calado aqui faria o operador aprovar achando que o kill switch está solto. */}
      {paused.isError && (
        <ErrorState
          error={paused.error}
          onRetry={() => {
            void paused.refetch();
          }}
        />
      )}

      {approve.error !== null && (
        <Notice tone="bad" title="A aprovação não foi aceita.">
          {errorMessageOf(approve.error)}
        </Notice>
      )}

      <Panel className="flex-1">
        <AdminWithdrawalsQueue
          rows={rows}
          tab={tab}
          onTabChange={(next) => {
            go({ status: next, ...(sellerId === undefined ? {} : { seller_id: sellerId }) });
          }}
          totals={totals}
          isLoading={list.isPending}
          error={list.error}
          onRetry={() => {
            void list.refetch();
          }}
          hasMore={list.hasNextPage}
          isLoadingMore={list.isFetchingNextPage}
          onLoadMore={() => {
            void list.fetchNextPage();
          }}
          onSelect={(withdrawal) => {
            void navigate({
              to: '/admin/withdrawals/$id',
              params: { id: withdrawal.id },
              search: { status: tab, ...(sellerId === undefined ? {} : { seller_id: sellerId }) },
            });
          }}
          onApprove={(withdrawal) => {
            approve.mutate(withdrawal);
          }}
          approvingId={approve.isPending ? approve.variables.id : undefined}
          {...(detail === undefined ? {} : { selectedId: detail.params.id })}
          {...(sellerId === undefined ? {} : { sellerId })}
          onClearSeller={() => {
            go({ status: tab });
          }}
        />
      </Panel>

      <RouteDrawer
        open={detail !== undefined}
        onClose={() => {
          go({ status: tab, ...(sellerId === undefined ? {} : { seller_id: sellerId }) });
        }}
        title="Detalhe do saque"
        {...(detail === undefined ? {} : { sub: <Mono>{detail.params.id}</Mono> })}
      >
        <Outlet />
      </RouteDrawer>
    </>
  );
}

export const Route = createFileRoute('/admin/withdrawals')({
  validateSearch: searchSchema,
  component: AdminWithdrawals,
});
